<template>
  <DetailPage>
    <template #left-nav>
      <button class="link link-main icon-link" @click="emit('onBack')">
        <Icon icon="left" /> {{ $t('GENERAL.BACK') }}
      </button>
    </template>
    <template #central-nav>
      <h2>{{ $t('PUBLISHING.DETAIL.PRODUCTS.SETTINGS_DIALOG.HEADING') }}</h2>
      <CompositionModeBadge :composition-mode="compositionMode" />
    </template>
    <template #right-nav>
      <SampleOrderButton :source="SAMPLE_ORDER_SOURCE.PRODUCT_SETTINGS" />
    </template>
    <template #content>
      <div class="product-settings">
        <div
          class="sellables"
          :class="{ 'has-details': detailProduct !== null }"
        >
          <div class="heading">
            <h1>
              {{ $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.SELLABLES.HEADING') }}
            </h1>
            <p>
              {{
                $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.SELLABLES.SUBHEADING')
              }}
            </p>
          </div>

          <div v-if="selectedSellables.length > 0" class="bulk-control">
            <p>
              {{ selectedSellables.length }}/{{ sellables.length }} selected
            </p>
            <Checkbox
              :model-value="selectedSellables.length === sellables.length"
              @update:model-value="toggleSellableSelectionForAll"
            >
              {{ $t('GENERAL.DELETE') }}
            </Checkbox>

            <button class="link-main icon-btn" @click="deleteSelectedSellables">
              <Icon icon="bin" />
              {{ $t('GENERAL.DELETE') }}
            </button>
          </div>

          <div v-if="sellables.length === 0" class="empty-state">
            <img src="/images/empty-assortment.png" alt="empty assortment" />
            <h2>
              {{
                $t(
                  'PUBLISHING.DETAIL.PRODUCTS.ADVANCED.SELLABLES.EMPTY.HEADING',
                )
              }}
            </h2>
            <p>
              {{
                $t(
                  'PUBLISHING.DETAIL.PRODUCTS.ADVANCED.SELLABLES.EMPTY.SUBHEADING',
                )
              }}
            </p>
            <button class="btn btn-primary" @click="emit('addNew')">
              {{
                $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.SELLABLES.EMPTY.CTA')
              }}
            </button>
          </div>
          <ul v-else>
            <li class="add-tile">
              <button class="add-button" @click="emit('addNew')">
                <div>
                  <Icon icon="plus" />
                  <h4>
                    {{
                      $t('PUBLISHING.DETAIL.PRODUCTS.PRODUCTS_CREATED.ADD_NEW')
                    }}
                  </h4>
                </div>
              </button>
            </li>
            <li v-for="sellable in sellables" :key="sellable.sellableId">
              <LazyAssortmentCard
                :product-type-id="sellable.productTypeId"
                :view-id="sellable.defaultViewId"
                :image="`${compositionHelper.getProductImage(
                  sellable.vpString,
                  sellable.defaultViewId,
                )}?width=250`"
                :appearance-ids="sellable.appearanceIds"
                :max-colors="1"
                :is-selectable="true"
                :selected="selectedSellables.includes(sellable)"
                :always-show-checkbox="selectedSellables.length > 0"
                :context-menu-options="contextMenuOptions"
                @edit="edit(sellable.productTypeId)"
                @toggle-selection="toggleSellableSelection(sellable)"
                @context-menu-click="
                  (option: { key: string; name: string }) =>
                    contextMenuClick(sellable, option)
                "
              />
            </li>
          </ul>
        </div>
        <Transition name="slide-in">
          <div v-if="detailProduct" class="product-details-wrapper">
            <ProductDetails
              :key="detailProduct"
              :product-type-id="detailProduct"
              :appearance-id="detailProductAppearanceId || undefined"
              @close="() => (detailProduct = null)"
              @select="edit(detailProduct)"
            />
          </div>
        </Transition>
      </div>
    </template>
  </DetailPage>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { useCompositionStore } from 'commons/stores/composition';
import type { Sellable } from '@spreadgroup/common-partner-lib/composition';
import compositionHelper from 'commons/composition/compositionHelper';
import DetailPage from 'src/app/components/detailPage/DetailPage.vue';
import CompositionModeBadge from 'src/app/components/publishing/statusBadges/CompositionModeBadge.vue';
import LazyAssortmentCard from './LazyAssortmentCard.vue';
import Checkbox from 'commons/checkbox/Checkbox.vue';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import ConfirmDialog from 'commons/dialogs/confirm/ConfirmDialog.vue';
import { i18n } from 'commons/translate/i18n';
import ProductDetails from './ProductDetails.vue';
import SampleOrderButton from 'src/app/partnerarea/publishing/detail/SampleOrderButton.vue';
import { SAMPLE_ORDER_SOURCE } from 'commons/sampleOrder/sampleOrderTracking';

const emit = defineEmits<{
  addNew: [];
  edit: [productType: number];
  onBack: [];
}>();

const { t } = i18n.global;
const compositionStore = useCompositionStore();
const compositionMode = computed(() => compositionStore.mode);
const sellables = computed(
  (): Sellable[] => compositionStore.current?.sellables || [],
);
const selectedSellables = ref<Sellable[]>([]);
const detailProduct = ref<number | null>(null);
const detailProductAppearanceId = ref<number | null>(null);

enum ContextMenuAction {
  PRODUCT_DETAILS = 'PRODUCT_DETAILS',
  DELETE = 'DELETE',
}
const contextMenuOptions = [
  {
    key: ContextMenuAction.PRODUCT_DETAILS,
    name: t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.PRODUCT_DETAILS.HEADING'),
  },
  { key: ContextMenuAction.DELETE, name: t('GENERAL.DELETE') },
];

const toggleSellableSelection = (sellable: Sellable) => {
  if (!selectedSellables.value.includes(sellable)) {
    selectedSellables.value.push(sellable);
  } else {
    selectedSellables.value = selectedSellables.value.filter(
      (s) => s !== sellable,
    );
  }
};

const edit = (productTypeId: number) => {
  detailProduct.value = null;
  emit('edit', productTypeId);
};

const toggleSellableSelectionForAll = (selectAll: boolean) => {
  if (selectAll) {
    selectedSellables.value = [...sellables.value];
  } else {
    selectedSellables.value = [];
  }
};

const deleteSelectedSellables = () => {
  dialogService
    .openDialog(ConfirmDialog, {
      heading:
        'PUBLISHING.DETAIL.PRODUCTS.ADVANCED.SELLABLES.DELETE_SELECTED_CONFIRMATION',
      cancel: 'GENERAL.DISCARD',
      confirm: 'GENERAL.DELETE',
    })
    .then(() => {
      compositionStore.deleteProductConfigs(
        selectedSellables.value.map((s) => s.productTypeId),
      );
      selectedSellables.value = [];
    });
};

const contextMenuClick = (
  sellable: Sellable,
  option: { key: string; name: string },
) => {
  switch (option.key) {
    case ContextMenuAction.DELETE:
      dialogService
        .openDialog(ConfirmDialog, {
          heading:
            'PUBLISHING.DETAIL.PRODUCTS.ADVANCED.SELLABLES.DELETE_CONFIRMATION',
          cancel: 'GENERAL.DISCARD',
          confirm: 'GENERAL.DELETE',
        })
        .then(() => {
          compositionStore.deleteProductConfigs([sellable.productTypeId]);
          selectedSellables.value = selectedSellables.value.filter(
            (s) => s !== sellable,
          );
        });
      break;
    case ContextMenuAction.PRODUCT_DETAILS:
      detailProduct.value = sellable.productTypeId;
      detailProductAppearanceId.value = sellable.appearanceIds?.[0];
      break;
  }
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/links';
@import 'src/scss/constants';

.product-settings {
  padding: 0 24px;
  width: 100%;

  .heading {
    margin-bottom: 16px;
  }

  h1 {
    margin: 0 0 8px 0;
  }

  p {
    margin: 0;
  }
}

.bulk-control {
  display: flex;
  align-items: center;
  gap: 32px;
  margin-bottom: 24px;

  p {
    margin: 0;
    font-weight: 700;
  }
}

.sellables {
  padding: 24px 0;

  &.has-details {
    padding-right: 350px + 24px;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 40px;

    h2 {
      margin: 0;
    }

    img {
      width: 200px;
      height: auto;
      margin-bottom: 8px;
    }
  }
}

.product-details-wrapper {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 350px;
  bottom: 24px;
}

ul {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin: 0;
  padding: 0;

  li {
    list-style: none;
  }
}

.add-tile {
  display: flex;
}

.add-button {
  background-color: $white;
  border-radius: $border-radius-medium;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  border: 1px solid $pa-color-main;
  color: $pa-color-main;

  &:hover {
    border-color: $pa-color-main-dark;
    color: $pa-color-main-dark;
  }

  .icon {
    width: 20px;
    height: 20px;
  }
}

// Slide-in animation from right
.slide-in-enter-active,
.slide-in-leave-active {
  transition: all 0.3s ease;
}

.slide-in-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-in-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
